import React, { useState, useEffect } from "react";
import { AiOutlineSearch, AiOutlineClose } from "react-icons/ai";
import axiosInstance from "../../axios/axios";
import { Encrypt_Value, Decrypt_Value } from "../../MiddleWare/EncryptDecrypt";
import "./NoteCodeSelectionPopup.css";

const NoteCodeSelectionPopup = ({
  show,
  onHide,
  codeType, // "DX", "CPT", "HCPCS"
  selectedCodes,
  activeProblemCodes = [],
  onCodesChange,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const getTitle = () => {
    switch (codeType) {
      case "DX":
        return "Diagnosis Code";
      case "CPT":
        return "CPT Code";
      case "HCPCS":
        return "HCPCS Code";
      default:
        return "Code Selection";
    }
  };

  const [suggestedCodes, setSuggestedCodes] = useState([]);
  const [isSuggestedLoading, setIsSuggestedLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setSearchTerm("");
      setSearchResults([]);
      fetchSuggestedCodes();
    }
  }, [show, codeType]);

  const formatClinicalHCPCS = (result) => {
    const list = Array.isArray(result?.[3]) ? result[3] : [];

    return list
      .map((item) => {
        const code = String(item?.[0] || "").trim();
        const description = String(item?.[1] || "").trim();

        if (!code) return null;

        return {
          code,
          description,
        };
      })
      .filter(Boolean);
  };

  const mergeCodesByCode = (localList, externalList) => {
    const mergedMap = new Map();

    [...localList, ...externalList].forEach((item) => {
      const code = String(item?.code || "").trim();

      if (!code) return;

      if (!mergedMap.has(code)) {
        mergedMap.set(code, item);
      }
    });

    return Array.from(mergedMap.values());
  };

  const fetchExternalHCPCS = async (term, limit = 50) => {
    const params = new URLSearchParams({
      terms: term,
      sf: "code,short_desc,long_desc",
      df: "code,display",
      maxList: String(limit),
    });

    const response = await fetch(
      `https://clinicaltables.nlm.nih.gov/api/hcpcs/v3/search?${params.toString()}`,
    );

    const result = await response.json();

    return formatClinicalHCPCS(result);
  };

  const fetchSuggestedCodes = async () => {
    setIsSuggestedLoading(true);
    let defaultSearchEnc = Encrypt_Value("limit=10&offset=0", "vozo");
    let url = "";

    if (codeType === "DX") {
      url = "/vozo/icd?search=" + defaultSearchEnc;
    } else if (codeType === "CPT") {
      url = "/vozo/cpt4?search=" + defaultSearchEnc;
    } else if (codeType === "HCPCS") {
      url = "/vozo/hcpcs?search=" + defaultSearchEnc;
    }

    try {
      const response = await axiosInstance.get(url);
      let decData = Decrypt_Value(response.data, "vozo");
      let data = [];
      if (Array.isArray(decData)) {
        data = decData.slice(0, 10).map((d) => {
          return {
            code: d.code || "",
            description: d.description || d.code_text || "",
          };
        });
      }

      if (codeType === "HCPCS") {
        try {
          const externalData = await fetchExternalHCPCS("", 10);
          data = mergeCodesByCode(data, externalData).slice(0, 10);
        } catch (err) {}
      }

      setSuggestedCodes(data);
    } catch (error) {
      console.error("Error fetching suggested codes:", error);
      setSuggestedCodes([]);
    } finally {
      setIsSuggestedLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.length >= 2) {
        fetchCodes(searchTerm);
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, codeType]);

  const fetchCodes = async (term) => {
    setIsLoading(true);
    let code_search_enc = Encrypt_Value(
      `limit=50&offset=0&code_name=${term.toLowerCase()}`,
      "vozo",
    );
    let url = "";

    if (codeType === "DX") {
      url = "/vozo/icd?search=" + code_search_enc;
    } else if (codeType === "CPT") {
      url = "/vozo/cpt4?search=" + code_search_enc;
    } else if (codeType === "HCPCS") {
      url = "/vozo/hcpcs?search=" + code_search_enc;
    }

    try {
      const response = await axiosInstance.get(url);
      let decData = Decrypt_Value(response.data, "vozo");
      let data = [];
      if (Array.isArray(decData)) {
        data = decData.map((d) => {
          return {
            code: d.code || "",
            description: d.description || d.code_text || "",
          };
        });
      }

      if (codeType === "HCPCS") {
        try {
          const externalData = await fetchExternalHCPCS(term, 50);
          data = mergeCodesByCode(data, externalData);
        } catch (err) {}
      }

      setSearchResults(data);
    } catch (error) {
      console.error("Error fetching codes:", error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const isCodeSelected = (code) => {
    return selectedCodes.some((c) => c.code === code && c.type === codeType);
  };

  const toggleCodeSelection = (codeObj) => {
    if (isCodeSelected(codeObj.code)) {
      onCodesChange(
        selectedCodes.filter(
          (c) => !(c.code === codeObj.code && c.type === codeType),
        ),
      );
    } else {
      onCodesChange([...selectedCodes, { ...codeObj, type: codeType }]);
    }
  };

  const renderCodeItem = (codeObj) => {
    const active = isCodeSelected(codeObj.code);
    return (
      <div
        key={codeObj.code}
        className={`code-item ${codeType === "DX" && active ? "active" : ""}`}
        onClick={() => toggleCodeSelection(codeObj)}
      >
        <div className="code-item-left">
          <span className="code-item-code">{codeObj.code}</span>
          <span className="code-item-desc">{codeObj.description}</span>
        </div>
        {codeType === "DX" && active && (
          <div className="code-item-active-badge">Active</div>
        )}
      </div>
    );
  };

  if (!show) return null;

  // Build a Set of active problem code strings to deduplicate ICD list
  const activeProblemCodeSet = new Set(activeProblemCodes.map((c) => c.code));

  // For ICD Codes section: remove codes already shown in Active Codes
  const filteredSuggestedCodes =
    codeType === "DX"
      ? suggestedCodes.filter((c) => !activeProblemCodeSet.has(c.code))
      : suggestedCodes;

  // For search results: bubble active codes to the top, rest below
  const activeSearchResults =
    codeType === "DX"
      ? searchResults.filter((c) => activeProblemCodeSet.has(c.code))
      : [];
  const remainingSearchResults =
    codeType === "DX"
      ? searchResults.filter((c) => !activeProblemCodeSet.has(c.code))
      : searchResults;

  return (
    <div className="code-selection-popup-wrapper">
      <div className="code-selection-header">
        <h5>{getTitle()}</h5>
        <AiOutlineClose className="close-icon" onClick={onHide} />
      </div>
      <div className="code-selection-body">
        <div className="search-input-container">
          <AiOutlineSearch className="search-icon" />
          <input
            type="text"
            placeholder={`Type to search ${codeType === "DX" ? "ICD" : codeType} codes`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="code-list-container">
          {searchTerm.length >= 2 ? (
            <>
              {isLoading ? (
                <div className="no-results">Searching...</div>
              ) : (
                <>
                  {/* Active problem codes that match the search — shown first */}
                  {codeType === "DX" && activeSearchResults.length > 0 && (
                    <div className="code-section">
                      <h6>Active Codes</h6>
                      {activeSearchResults.map(renderCodeItem)}
                    </div>
                  )}
                  <div className="code-section">
                    <h6>Search Results</h6>
                    {remainingSearchResults.length > 0 ? (
                      remainingSearchResults.map(renderCodeItem)
                    ) : (
                      <div className="no-results">No results found</div>
                    )}
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              {/* Active patient problems — always shown first for DX */}
              {codeType === "DX" && activeProblemCodes.length > 0 && (
                <div className="code-section">
                  <h6>Active Codes</h6>
                  {activeProblemCodes.map(renderCodeItem)}
                </div>
              )}
              {isSuggestedLoading ? (
                <div className="no-results">Loading suggestions...</div>
              ) : (
                <div className="code-section">
                  <h6>
                    {codeType === "DX" ? "ICD Codes" : `${codeType} Codes`}
                  </h6>
                  {filteredSuggestedCodes.length > 0 ? (
                    filteredSuggestedCodes.map(renderCodeItem)
                  ) : (
                    <div className="no-results">No suggestions found.</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NoteCodeSelectionPopup;
